// Cave84 — TransferBottleModal (Phase 1 Week 4)
// Move a bottle between cellars (F1.6). Creates a cellar_transfers audit log.

import { useState } from 'react';
import type { Bottle } from '@cave84/shared-types';
import { useCellars } from '@/hooks/useCellars';
import { useCellarLocations } from '@/hooks/useLocations';
import { useTransferBottle } from '@/hooks/useBottles';
import { useWine } from '@/hooks/useWines';
import { useVintage } from '@/hooks/useVintages';

interface Props {
  bottle: Bottle;
  onClose: () => void;
}

export default function TransferBottleModal({ bottle, onClose }: Props) {
  const [toCellarId, setToCellarId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [reason, setReason] = useState('');

  const { data: vintage } = useVintage(bottle.vintage_id);
  const { data: wine } = useWine(vintage?.wine_id);
  const { data: cellars = [] } = useCellars();
  const { data: locations = [] } = useCellarLocations(toCellarId || undefined);
  const transfer = useTransferBottle();

  // Exclude the bottle's current cellar from targets
  const targetCellars = cellars.filter(c => c.id !== bottle.current_cellar_id);

  const label = wine && vintage ? `${vintage.year} ${wine.cuvee_name}` : 'this bottle';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!toCellarId) return;
    await transfer.mutateAsync({
      bottle,
      toCellarId,
      toLocationId: toLocationId || undefined,
      reason: reason.trim() || undefined,
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-surface-1 rounded-xl border border-border-medium w-full max-w-sm p-6 shadow-xl">
        <h2 className="text-lg font-bold text-text-primary mb-1">Transfer Bottle</h2>
        <p className="text-sm text-text-secondary mb-5">
          Move <span className="font-medium text-text-primary">{label}</span> to another cellar
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <span className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wide">
              Destination Cellar
            </span>
            <select
              value={toCellarId}
              onChange={e => { setToCellarId(e.target.value); setToLocationId(''); }}
              required
              className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold"
            >
              <option value="" disabled>Select cellar…</option>
              {targetCellars.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {locations.length > 0 && (
            <div>
              <span className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wide">
                Location (optional)
              </span>
              <select
                value={toLocationId}
                onChange={e => setToLocationId(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary text-sm focus:outline-none focus:border-gold"
              >
                <option value="">None</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <span className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wide">
              Reason (optional)
            </span>
            <input
              type="text"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Travelling, gifting storage…"
              className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary text-sm focus:outline-none focus:border-gold"
            />
          </div>

          {transfer.isError && (
            <p className="text-xs text-red-400">{(transfer.error as Error).message}</p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={transfer.isPending}
              className="flex-1 px-4 py-2 rounded-md border border-border-medium bg-surface-2 text-text-primary text-sm hover:bg-surface-3 transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={transfer.isPending || !toCellarId}
              className="flex-1 px-4 py-2 rounded-md bg-gold text-black font-semibold text-sm hover:bg-gold/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {transfer.isPending ? 'Transferring…' : 'Transfer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
